/*
  Verknüpfungen — ein Format für alles, woran ein Termin hängen kann.

  Eine Verknüpfung ist ein Paar: `{ art, id }`.

      art = "vorlage"  → eine Vorlage der Verwaltung
      art = "top"      → ein Tagesordnungspunkt
      art = "aufgabe"  → eine Aufgabe der Fraktion
      art = "datei"    → eine abgelegte Datei der Fraktion

  Warum ein Format und nicht vier Beziehungsfelder auf dem Server: Sonst wäre
  jede neue verknüpfbare Sache eine eigene Wanderung, und die Oberfläche
  bräuchte vier Auswahlfelder statt einem. So genügt für Notizen oder Anträge
  später ein weiterer Eintrag in diesem Auflöser.

  Der Preis: Der Server prüft die Verweise nicht. Ein Verweis auf eine
  gelöschte Datei ist deshalb kein Fehlerfall, sondern ein Regelfall — er
  liefert `fehlt: true`, und die Oberfläche sagt es. Eine weiße Seite wäre
  die schlechtere Antwort.
*/

export const ARTEN = [
  { art: "top", name: "Tagesordnungspunkt" },
  { art: "vorlage", name: "Vorlage" },
  { art: "aufgabe", name: "Aufgabe" },
  { art: "datei", name: "Datei" },
];

/*
  Ein Paar auflösen: Was ist das, wie heißt es, und wo findet man es?

  `oeffne` bekommt die Sprungfunktion der Anwendung und wird nur gesetzt,
  wenn es dort auch etwas zu sehen gibt.
*/
export function loeseAuf({ art, id }, stammdaten, zustand) {
  if (art === "top") {
    const top = (stammdaten.tagesordnung || []).find((t) => t.id === id);
    if (!top) return fehlend(art, "Tagesordnungspunkt");
    return {
      art,
      id,
      titel: `TOP ${top.nummer} · ${top.titel}`,
      ziel: ["sitzung", { topId: top.id, reiter: "vorlage" }],
    };
  }

  if (art === "vorlage") {
    const vorlage = (stammdaten.vorlagen || {})[id];
    if (!vorlage) return fehlend(art, "Vorlage");
    const top = (stammdaten.tagesordnung || []).find((t) => t.vorlageId === id);
    return {
      art,
      id,
      titel: `${vorlage.aktenzeichen} · ${vorlage.kurztitel || vorlage.titel}`,
      ziel: top ? ["sitzung", { topId: top.id, reiter: "vorlage" }] : null,
    };
  }

  if (art === "aufgabe") {
    const aufgabe = (zustand.aufgaben || []).find((a) => a.id === id);
    if (!aufgabe) return fehlend(art, "Aufgabe");
    return {
      art,
      id,
      titel: aufgabe.titel,
      ziel: ["aufgaben", { aufgabeId: aufgabe.id }],
    };
  }

  if (art === "datei") {
    const datei = (zustand.ablage || []).find((d) => d.id === id);
    if (!datei) return fehlend(art, "Datei");
    return {
      art,
      id,
      titel: datei.titel || datei.datei || "Datei",
      ziel: ["dateien", {}],
    };
  }

  return fehlend(art, "Eintrag");
}

function fehlend(art, was) {
  return { art, titel: `${was} nicht mehr vorhanden`, fehlt: true, ziel: null };
}

/*
  Was lässt sich gerade verknüpfen? Für die Auswahl beim Anlegen.

  Bewusst ohne Suchfeld: Eine Fraktion hat neun Tagesordnungspunkte, ein
  paar Vorlagen und selten mehr als zwanzig Aufgaben. Ein Suchfeld wäre eine
  Hürde vor einer kurzen Liste.
*/
export function waehlbares(art, stammdaten, zustand, ichId) {
  if (art === "top") {
    return (stammdaten.tagesordnung || []).map((t) => ({
      id: t.id,
      titel: `TOP ${t.nummer} · ${t.titel}`,
    }));
  }

  if (art === "vorlage") {
    return Object.values(stammdaten.vorlagen || {})
      .filter((v) => !v.archiviert)
      .map((v) => ({ id: v.id, titel: `${v.aktenzeichen} · ${v.kurztitel || v.titel}` }));
  }

  if (art === "aufgabe") {
    return (zustand.aufgaben || [])
      .filter((a) => a.zustaendigId === ichId || (a.beobachterIds || []).includes(ichId))
      .map((a) => ({ id: a.id, titel: a.titel }));
  }

  if (art === "datei") {
    return (zustand.ablage || []).map((d) => ({ id: d.id, titel: d.titel || d.datei }));
  }

  return [];
}
